/* eslint-disable react/destructuring-assignment */
/* eslint-disable react/require-default-props */
/* eslint-disable react-hooks/exhaustive-deps */
import { Group, Input, NumberInput, Select } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconRuler2 } from '@tabler/icons-react';
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { isNullOrEmpty } from '../utils/helper.utils';
import { ComboBoxDataV5, transformComboBoxGroupDataFromV5toV7 } from '../utils/mantine.utils';
import PageViewProperty from './core/PageViewProperty/PageViewProperty';

type SelectOptionType = {
  value: string;
  label: string;
  group?: string;
};

export type BasicValueUnitsData = {
  valor: number | null;
  idUnidade1: string | null;
  idUnidade2: string | null;
};

export type BasicValueUnitsRef = {
  validate(): BasicValueUnitsData | null;
  clear(): void;
};

type BasicValueUnitsProps = {
  isEdit: boolean;
  disabled?: boolean;
  disableValue?: boolean;
  disableUnit1?: boolean;
  disableUnit2?: boolean;
  zIndex?: number;
  label: string;
  placeholderValor?: string;
  showValue?: boolean;
  showUnit2?: boolean;
  validationMode?: 'all' | 'all-or-none';
  valueMin?: number;
  valueDefaultValue?: number;
  valueDecimalScale?: number;
  valueStep?: number;
  valueMustBePositiveWhenFilled?: boolean;
  value: BasicValueUnitsData;
  unit1Options: ComboBoxDataV5[];
  unit2Options?: ComboBoxDataV5[];
  onChange?: (value: BasicValueUnitsData) => void;
};

function resolveOptionLabel(value: string | null, options: SelectOptionType[] | undefined): string {
  if (!value || !options) {
    return '';
  }

  return (options.find((option) => option.value === value)?.label || '').split(' ')[0];
}

function formatNumber(value: number | null): string {
  if (isNullOrEmpty(value)) {
    return '';
  }

  return Number(value).toFixed(2);
}

function normalizeNumericValue(value: unknown): number | null {
  if (isNullOrEmpty(value) || value === '') {
    return null;
  }

  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
}

function normalizeSelectValue(value: string | null | undefined): string | null {
  return isNullOrEmpty(value) ? null : value!;
}

function serializeValueUnits(value: BasicValueUnitsData): string {
  return JSON.stringify(value);
}

const BasicValueUnits = forwardRef((props: BasicValueUnitsProps, ref) => {
  const { idUnidade1, idUnidade2, valor } = props.value;
  const lastEmittedValueRef = useRef<string | null>(null);
  const onChangeRef = useRef(props.onChange);
  const showValue = props.showValue ?? true;
  const showUnit2 = props.showUnit2 ?? true;
  const validationMode = props.validationMode || 'all';
  const shouldConsiderUnit1AsTrigger = validationMode === 'all-or-none' && !props.disableUnit1;
  const shouldConsiderUnit2AsTrigger = validationMode === 'all-or-none' && showUnit2 && !props.disableUnit2;

  const normalizeValueUnits = (values: BasicValueUnitsData): BasicValueUnitsData => ({
    valor: showValue ? normalizeNumericValue(values.valor) : null,
    idUnidade1: normalizeSelectValue(values.idUnidade1),
    idUnidade2: showUnit2 ? normalizeSelectValue(values.idUnidade2) : null,
  });

  useEffect(() => {
    onChangeRef.current = props.onChange;
  }, [props.onChange]);

  const form = useForm<BasicValueUnitsData>({
    initialValues: normalizeValueUnits(props.value),
    validate: {
      valor: (value, values) => {
        if (!showValue) {
          return null;
        }

        const normalizedValue = normalizeNumericValue(value);
        const hasAnyField =
          !isNullOrEmpty(normalizedValue) ||
          (shouldConsiderUnit1AsTrigger && !!values.idUnidade1) ||
          (shouldConsiderUnit2AsTrigger && !!values.idUnidade2);

        if (validationMode === 'all') {
          if (isNullOrEmpty(normalizedValue)) {
            return 'Campo obrigatório';
          }
        } else if (hasAnyField && isNullOrEmpty(normalizedValue)) {
          return 'Campo obrigatório';
        }

        if (
          props.valueMustBePositiveWhenFilled &&
          hasAnyField &&
          !isNullOrEmpty(normalizedValue) &&
          normalizedValue! <= 0
        ) {
          return 'Valor deve ser maior do que zero';
        }

        if (!isNullOrEmpty(normalizedValue) && normalizedValue! < (props.valueMin ?? 0)) {
          return `Valor deve ser maior ou igual a ${props.valueMin ?? 0}`;
        }

        return null;
      },
      idUnidade1: (value, values) => {
        const hasAnyField =
          (showValue && !isNullOrEmpty(normalizeNumericValue(values.valor))) ||
          (shouldConsiderUnit2AsTrigger && !!values.idUnidade2);

        if (validationMode === 'all') {
          return !value ? 'Campo obrigatório' : null;
        }

        if (hasAnyField && !value) {
          return 'Campo obrigatório';
        }

        return null;
      },
      idUnidade2: (value, values) => {
        if (!showUnit2) {
          return null;
        }

        const hasAnyField =
          (showValue && !isNullOrEmpty(normalizeNumericValue(values.valor))) ||
          (shouldConsiderUnit1AsTrigger && !!values.idUnidade1) ||
          !!value;

        if (validationMode === 'all') {
          return !value ? 'Campo obrigatório' : null;
        }

        if (hasAnyField && !value) {
          return 'Campo obrigatório';
        }

        return null;
      },
    },
  });

  useEffect(() => {
    const nextExternalValue = normalizeValueUnits({
      valor,
      idUnidade1,
      idUnidade2,
    });
    const nextExternalValueKey = serializeValueUnits(nextExternalValue);
    const currentFormValueKey = serializeValueUnits(normalizeValueUnits(form.values));

    if (nextExternalValueKey === currentFormValueKey) {
      return;
    }

    if (nextExternalValueKey === lastEmittedValueRef.current) {
      lastEmittedValueRef.current = null;
      return;
    }

    form.setValues(nextExternalValue);
  }, [idUnidade1, idUnidade2, valor]);

  useEffect(() => {
    const nextValue = normalizeValueUnits(form.values);

    lastEmittedValueRef.current = serializeValueUnits(nextValue);
    onChangeRef.current?.(nextValue);
  }, [form.values.idUnidade1, form.values.idUnidade2, form.values.valor, showUnit2, showValue]);

  useImperativeHandle(ref, () => ({
    validate() {
      if (form.validate().hasErrors) {
        return null;
      }

      return normalizeValueUnits(form.values);
    },
    clear() {
      form.setValues({ valor: null, idUnidade1: null, idUnidade2: null });
      form.clearErrors();
    },
  }));

  if (!props.isEdit) {
    const normalizedValues = normalizeValueUnits(form.values);
    const valueText = showValue ? formatNumber(normalizedValues.valor) : '';
    const unit1Text = resolveOptionLabel(normalizedValues.idUnidade1, props.unit1Options);
    const unit2Text = showUnit2 ? resolveOptionLabel(normalizedValues.idUnidade2, props.unit2Options) : '';

    return (
      <PageViewProperty
        label={props.label}
        text={`${
          valueText && showValue
            ? `${valueText} ${unit1Text} ${unit2Text && showUnit2 ? ` / ${unit2Text}` : ''}`
            : '-'
        }`}
        size="sm"
      />
    );
  }

  return (
    <div>
      <Input.Wrapper label={props.label} required={validationMode === 'all'}>
        <div />
      </Input.Wrapper>
      <Group gap="xs">
        {showValue && (
          <NumberInput
            style={{ width: 100 }}
            placeholder={props.placeholderValor || props.label}
            min={props.valueMin ?? 0}
            defaultValue={props.valueDefaultValue}
            decimalScale={props.valueDecimalScale ?? 2}
            step={props.valueStep ?? 0.5}
            stepHoldDelay={500}
            stepHoldInterval={(t) => Math.max(1000 / t ** 2, 25)}
            required={validationMode === 'all'}
            hideControls={props.disabled || props.disableValue}
            disabled={props.disabled || props.disableValue}
            error={form.errors.valor}
            value={form.values.valor ?? ''}
            onChange={(value) => form.setFieldValue('valor', normalizeNumericValue(value))}
          />
        )}
        <Select
          leftSection={<IconRuler2 size={15} />}
          placeholder="Selecione..."
          data={transformComboBoxGroupDataFromV5toV7(props.unit1Options)}
          clearable={validationMode === 'all-or-none'}
          searchable
          required={validationMode === 'all'}
          disabled={props.disabled || props.disableUnit1}
          error={form.errors.idUnidade1}
          value={form.values.idUnidade1}
          onChange={(value) => form.setFieldValue('idUnidade1', normalizeSelectValue(value))}
          comboboxProps={{ zIndex: props.zIndex }}
        />
        {showUnit2 && (
          <>
            <span>/</span>
            <Select
              leftSection={<IconRuler2 size={15} />}
              placeholder="Selecione..."
              data={transformComboBoxGroupDataFromV5toV7(props.unit2Options || [])}
              clearable={validationMode === 'all-or-none'}
              searchable
              required={validationMode === 'all'}
              disabled={props.disabled || props.disableUnit2}
              error={form.errors.idUnidade2}
              value={form.values.idUnidade2}
              onChange={(value) => form.setFieldValue('idUnidade2', normalizeSelectValue(value))}
              comboboxProps={{ zIndex: props.zIndex }}
            />
          </>
        )}
      </Group>
    </div>
  );
});

export default BasicValueUnits;
